import { useParams, Link, Navigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { GLOSSARY_BY_SLUG, GLOSSARY } from "@/data/glossaryData";
import { LOGO_URL } from "@/config";
import { Button } from "@/components/ui/button";
import { ArrowLeft, BookMarked } from "lucide-react";

const SITE = "https://creatara.ai";

export default function GlossaryTermPage() {
  const { slug } = useParams();
  const entry = GLOSSARY_BY_SLUG[slug];
  if (!entry) return <Navigate to="/glossary" replace />;

  const url = `${SITE}/glossary/${entry.slug}`;
  const definedTermSchema = {
    "@context": "https://schema.org",
    "@type": "DefinedTerm",
    name: entry.term,
    description: entry.summary,
    inDefinedTermSet: { "@type": "DefinedTermSet", name: "Creatara Glossary", url: `${SITE}/glossary` },
    url,
  };
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: `What is ${entry.term}?`,
        acceptedAnswer: { "@type": "Answer", text: entry.summary },
      },
    ],
  };
  const related = (entry.related || []).map((s) => GLOSSARY_BY_SLUG[s]).filter(Boolean);

  return (
    <>
      <Helmet>
        <title>{entry.metaTitle}</title>
        <meta name="description" content={entry.metaDescription} />
        <link rel="canonical" href={url} />
        <meta property="og:title" content={entry.metaTitle} />
        <meta property="og:description" content={entry.metaDescription} />
        <meta property="og:url" content={url} />
        <meta property="og:type" content="article" />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify(definedTermSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <div className="min-h-screen bg-background">
        <nav className="fixed top-0 w-full z-50 glass">
          <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <img src={LOGO_URL} alt="Creatara" className="w-10 h-10 rounded-lg" />
              <span className="font-bold text-lg">Creatara</span>
            </Link>
            <Link to="/auth"><Button size="sm" className="rounded-full bg-violet-600 hover:bg-violet-700">Try Free</Button></Link>
          </div>
        </nav>

        <article className="max-w-3xl mx-auto px-6 pt-32 pb-20" data-testid={`glossary-term-${entry.slug}`}>
          <Link to="/glossary" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-white mb-6">
            <ArrowLeft className="w-4 h-4" /> All definitions
          </Link>

          <span className="inline-flex items-center gap-1 text-xs uppercase tracking-wider text-amber-400 mb-3">
            <BookMarked className="w-3 h-3" /> Glossary
          </span>
          <h1 className="text-4xl sm:text-5xl font-bold mb-6">{entry.term}</h1>

          <p className="text-lg leading-relaxed text-muted-foreground bg-white/5 border-l-4 border-violet-500 pl-5 py-4 rounded-r mb-10">
            {entry.summary}
          </p>

          <div className="prose prose-invert max-w-none">
            {entry.body.split("\n\n").map((p, i) => (
              <p key={i} className="text-base leading-relaxed mb-4 text-muted-foreground whitespace-pre-line">{p}</p>
            ))}
          </div>

          <div className="my-12 rounded-xl bg-gradient-to-br from-violet-600/20 to-amber-600/10 border border-violet-500/30 p-8 text-center">
            <h3 className="text-2xl font-bold mb-2">See {entry.term} in action</h3>
            <p className="text-muted-foreground mb-5">Creatara is the only platform that combines AI content with this capability.</p>
            <Link to="/auth"><Button size="lg" className="rounded-full bg-violet-600 hover:bg-violet-700">Try free →</Button></Link>
          </div>

          {related.length > 0 && (
            <section className="border-t border-white/10 pt-8">
              <h3 className="font-semibold mb-4">Related terms</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {related.map((r) => (
                  <Link key={r.slug} to={`/glossary/${r.slug}`} className="block rounded-lg bg-white/5 border border-white/10 hover:border-violet-500/40 p-4">
                    <p className="font-medium">{r.term}</p>
                    <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{r.summary}</p>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </article>
      </div>
    </>
  );
}
